/**
 * @file Axios request interceptor for automatic token lifecycle management.
 *
 * Before every outgoing request, reads the non-httpOnly `refresh_at` cookie to
 * determine whether tokens are missing or stale. If so, all outgoing requests
 * are gated behind a single shared refresh call. This covers both CDN cache
 * hits (no cookies at all) and mid-session expiration (tokens expired while
 * idle), preventing concurrent server-side token calls that would race against
 * SLAS rate limits.
 *
 * The actual refresh logic and cross-tab coordination live in
 * `core.ts` — this module is a thin adapter that wires the core
 * into the Axios interceptor lifecycle.
 *
 * @see core.ts  — shared state, cookie check, cross-tab lock
 * @see events.ts — proactive browser-event refresh
 */

import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { needsRefresh, ensureRefresh } from './core';
import { hasReloadFlag, clearReloadFlag } from './utils';
import { TOKEN_REFRESH_CONFIG } from './config';

/**
 * Applies a request interceptor to an Axios instance for automatic token
 * lifecycle management.
 *
 * Safeguards against infinite loops:
 * - Concurrent requests share one refresh promise (no duplicate calls).
 * - Cross-tab coordination via localStorage lock (see `core.ts`).
 * - If the refresh endpoint itself fails, triggers a page reload with a
 *   sessionStorage guard to prevent infinite reload loops.
 *
 * @param instance - The Axios instance to apply the interceptor to.
 * @param refreshUrl - The URL to call to refresh the token. Defaults to '/api/auth/refresh-tokens'.
 * @returns The same Axios instance with the interceptor applied (for chaining).
 */
export function applyTokenRefreshInterceptor(
  instance: AxiosInstance,
  refreshUrl: string = TOKEN_REFRESH_CONFIG.defaultRefreshUrl
): AxiosInstance {
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // If the page just reloaded due to a failed refresh, don't try again.
      if (hasReloadFlag()) {
        clearReloadFlag();
        return config;
      }

      if (needsRefresh()) {
        await ensureRefresh(refreshUrl);
      }

      return config;
    }
  );

  return instance;
}
